// src/new/components/monitor/TriageChat/TriageChat.tsx
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IconButton, TextField, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { TriageContext, triageContextChipLabel, buildTriageSeed } from './triageContext';
import { extractFollowUps } from './followUps';
import { ChatMessage } from './useVedaChatSocket';
import { TriageSubagentBubble } from './TriageSubagentBubble';
import { rawPayloadMarkdownOptions } from './markdownOptions';
import { wrapRawPayloadBlocks } from './payloadBlocks';
import s from './TriageChat.module.scss';

export interface TriageChatProps {
  context: TriageContext;
  messages: ChatMessage[];
  connected: boolean;
  streaming: boolean;
  /** True while an express-code turn is waiting on a cold Daytona sandbox
   *  boot (15-30s). Can recur on ANY turn — sandboxes sleep after idle — so
   *  this is not a "first turn only" state. Always `false` for the veda-ai
   *  backend (defaulted here so existing veda-only call sites/tests that
   *  don't pass it keep working unchanged). */
  waitingForReady?: boolean;
  /** True while a mount-time `/tail` resume probe (reopening the widget on a
   *  reused express-code session) is outstanding — distinct from
   *  `waitingForReady`: this is "did we miss anything while closed," not "is
   *  the sandbox cold." Always `false` for the veda-ai backend. */
  reconnecting?: boolean;
  /** True only for express-code: sending while `streaming` there aborts the
   *  in-flight turn (its SSE transport is one request per turn — see
   *  `useExpressChatSSE.sendUserMessage`'s `controllerRef.current?.abort()`),
   *  so the composer must stay disabled until it settles. veda-ai's
   *  persistent WebSocket has no such hazard, so this defaults to `false` —
   *  keeping its pre-existing "disabled only while disconnected" behavior
   *  unchanged rather than blocking mid-thought follow-ups as a side effect
   *  of an unrelated backend migration. */
  blockSendWhileStreaming?: boolean;
  error: string | null;
  onSend: (text: string, displayText?: string) => void;
  onStop?: () => void;
  /** False for a click-opened session — shows the "Check current status"
   *  primary action in the empty state. Defaults to `true` (deep-link /
   *  existing call sites), matching `useTriageChat`'s own default, so no
   *  button shows up unless a caller explicitly opts in to click-opened
   *  behavior. */
  autoSeed?: boolean;
}

const AgentAvatar: React.FC = () => (
  <div className={s.agentAvatar} aria-hidden="true">
    <TimelineOutlinedIcon className={s.agentAvatarIcon} />
  </div>
);

/** Keeps content aligned under the avatar column without repeating the agent
 *  icon — used by tool/thinking rows (which are sub-steps, not the agent
 *  "speaking") and by continued assistant bubbles, so the icon shows once per
 *  agent turn instead of on every row. */
const AvatarSpacer: React.FC = () => <div className={s.avatarSpacer} aria-hidden="true" />;

/** HH:MM timestamp, rendered subtly under a turn. */
const Timestamp: React.FC<{ at?: number }> = ({ at }) => {
  if (!at) return null;
  const d = new Date(at);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return <div className={s.timestamp}>{`${hh}:${mm}`}</div>;
};

const UserBubble = React.memo<{ m: ChatMessage }>(({ m }) => (
  <div className={s.userWrap}>
    <div className={s.user}>{m.text}</div>
    <Timestamp at={m.createdAt} />
  </div>
));
UserBubble.displayName = 'UserBubble';

const AssistantBubble = React.memo<{ m: ChatMessage; showAvatar?: boolean }>(
  ({ m, showAvatar = true }) => {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(() => {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
      navigator.clipboard.writeText(m.text ?? '').then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        },
        () => {},
      );
    }, [m.text]);

    const subagents = m.subagents ? Object.values(m.subagents) : [];
    const displayText = useMemo(() => wrapRawPayloadBlocks(m.text), [m.text]);

    return (
      <div className={s.messageRow}>
        {showAvatar ? <AgentAvatar /> : <AvatarSpacer />}
        <div className={s.assistantCol}>
          <div className={s.assistant}>
            <MarkdownContent options={rawPayloadMarkdownOptions}>{displayText}</MarkdownContent>
          </div>
          <div className={s.assistantMeta}>
            <Timestamp at={m.createdAt} />
            <button
              type="button"
              className={s.copyButton}
              onClick={copy}
              aria-label="Copy message"
              data-testid="triage-copy"
            >
              {copied ? 'Copied' : '⧉'}
            </button>
          </div>
          {subagents.length > 0 && (
            <div className={s.subagentList} data-testid="triage-subagent-list">
              {subagents.map((subagentState) => (
                <TriageSubagentBubble key={subagentState.childSessionId} subagent={subagentState} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);
AssistantBubble.displayName = 'AssistantBubble';

/** Collapses a run of consecutive tool calls into one compact, expandable row
 *  (instead of a flat list of "⚙ …" lines). */
const ToolGroup = React.memo<{ tools: string[]; active: boolean }>(({ tools, active }) => {
  const [open, setOpen] = useState(false);
  const label = active ? 'Working' : 'Ran';
  return (
    <div className={s.messageRow} data-testid="triage-tool-row">
      <AvatarSpacer />
      <div className={s.toolRow}>
        <button
          type="button"
          className={s.toolGroupToggle}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {active ? (
            <CircularProgress size={12} className={s.toolSpinner} />
          ) : (
            <span aria-hidden="true">⚙</span>
          )}
          <span>
            {label} {tools.length} step{tools.length === 1 ? '' : 's'}
          </span>
          <span className={s.toolCaret} aria-hidden="true">
            {open ? '▴' : '▾'}
          </span>
        </button>
        {open && (
          <ul className={s.toolGroupList}>
            {tools.map((t, i) => (
              <li key={`${t}-${i}`}>{t}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
});
ToolGroup.displayName = 'ToolGroup';

type RenderItem =
  | { kind: 'msg'; m: ChatMessage }
  | { kind: 'tools'; id: string; tools: string[] }
  | { kind: 'status'; label: string };

/** Fold consecutive tool messages into a single group item. */
function groupMessages(messages: ChatMessage[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (const m of messages) {
    if (m.role === 'tool') {
      const last = items[items.length - 1];
      if (last && last.kind === 'tools') last.tools.push(m.text);
      else items.push({ kind: 'tools', id: m.id, tools: [m.text] });
    } else {
      items.push({ kind: 'msg', m });
    }
  }
  return items;
}

const STARTER_PROMPTS = [
  'Anything failing right now?',
  'Show recent errors',
  'What changed recently?',
];

/** Fires the funnel event a click-opened session's empty-state action was
 *  picked. Named per the design doc's deferred follow-up
 *  (plans/2026-07-28-triage-suggested-actions-design.md) — only reachable
 *  when `!autoSeed`, since auto-seeded sessions never show this empty state. */
function trackSuggestedAction(context: TriageContext, action: string): void {
  AnalyticsUtils.captureCustomEvent('triage_suggested_action_clicked', {
    action,
    resource_type: context.resourceType,
    resource_id: context.resourceId,
    has_run_id: !!context.runId,
  });
}

export const TriageChat: React.FC<TriageChatProps> = ({
  context,
  messages,
  connected,
  streaming,
  waitingForReady = false,
  reconnecting = false,
  blockSendWhileStreaming = false,
  error,
  onSend,
  onStop,
  autoSeed = true,
}) => {
  const [draft, setDraft] = useState('');

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  };

  const items = groupMessages(messages);
  const lastIsTools = items.length > 0 && items[items.length - 1].kind === 'tools';
  // Transient status row, most specific state first: a resume probe
  // (`reconnecting`) implies the hook hasn't even resolved session setup yet,
  // so it takes precedence over a cold sandbox boot, which in turn takes
  // precedence over the generic "thinking…" row shown while streaming before
  // the first delta / when the run isn't currently a tool group (which
  // renders its own spinner). `reconnecting`/`waitingForReady` always win over
  // the tool-group suppression too — a fresh sandbox boot or resume probe can
  // land after a prior turn's tool group without a live turn (yet) to attach
  // its own spinner to.
  if (reconnecting) {
    items.push({ kind: 'status', label: 'Reconnecting…' });
  } else if (waitingForReady) {
    items.push({ kind: 'status', label: 'Starting AI environment…' });
  } else if (streaming && !lastIsTools) {
    items.push({ kind: 'status', label: 'thinking…' });
  }

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [atBottom, setAtBottom] = useState(true);

  const itemContent = useCallback(
    (index: number, it: RenderItem) => {
      if (it.kind === 'tools') {
        return <ToolGroup tools={it.tools} active={streaming && index === items.length - 1} />;
      }
      if (it.kind === 'status') {
        return (
          <div className={s.messageRow} data-testid="triage-status-row">
            <AvatarSpacer />
            <div className={s.toolRow}>
              <span className={s.tool}>
                <CircularProgress size={12} /> {it.label}
              </span>
            </div>
          </div>
        );
      }
      const m = it.m;
      if (m.role === 'user') return <UserBubble m={m} />;
      // Show the agent icon once per run: hide it when the previous item is also
      // an assistant bubble (a continued segment), so we don't stack bulbs.
      const prev = items[index - 1];
      const showAvatar = !(prev && prev.kind === 'msg' && prev.m.role === 'assistant');
      return <AssistantBubble m={m} showAvatar={showAvatar} />;
    },
    // items.length is needed so the last tool group spinner toggles correctly.
    [streaming, items.length],
  );

  // Follow-up action chips: only when idle and the last turn is the assistant.
  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
  const followUps =
    !streaming && lastMsg && lastMsg.role === 'assistant' ? extractFollowUps(lastMsg.text) : [];

  const lastUserText = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') return messages[i].text;
    }
    return null;
  })();

  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const showError = !!error && error !== dismissedError;

  const showEmptyState =
    messages.length === 0 && !streaming && !error && !waitingForReady && !reconnecting;

  // `connected` meant "WebSocket open" for veda-ai — still meaningful there,
  // so it stays part of the gate. It's meaningless with express-code's
  // SSE-per-turn transport, where the real gate is "sandbox is booting" or
  // "resume probe in flight" — `waitingForReady`/`reconnecting` cover that
  // (always `false` for veda-ai, so they're no-ops there). `streaming` only
  // joins the gate when the caller opts in via `blockSendWhileStreaming`
  // (express-code only, where sending mid-stream would abort the turn) —
  // veda-ai's "disabled until connected" behavior is otherwise unchanged.
  const composerDisabled =
    !connected || waitingForReady || reconnecting || (blockSendWhileStreaming && streaming);
  const composerPlaceholder = reconnecting
    ? 'Reconnecting…'
    : waitingForReady
      ? 'Starting AI environment…'
      : connected
        ? 'Ask a question…'
        : 'Connecting…';

  return (
    <div className={s.root}>
      <div className={s.chip} data-testid="triage-context-chip">
        Context: <strong>{triageContextChipLabel(context)}</strong>
        {context.runId ? ` · run ${context.runId}` : ''}
      </div>

      <div className={s.messagesWrap} data-testid="triage-chat-messages">
        {showEmptyState ? (
          <div className={s.emptyState}>
            <AgentAvatar />
            <div className={s.emptyTitle}>How can I help you triage this?</div>
            <div className={s.starterRow}>
              {!autoSeed && (
                <button
                  type="button"
                  className={`${s.starterChip} ${s.starterChipPrimary}`}
                  onClick={() => {
                    trackSuggestedAction(context, 'check_current_status');
                    const seed = buildTriageSeed(context);
                    onSend(seed.sent, seed.display);
                  }}
                  data-testid="triage-check-status"
                  disabled={!connected}
                >
                  Check current status
                </button>
              )}
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={s.starterChip}
                  onClick={() => {
                    trackSuggestedAction(context, p);
                    onSend(p);
                  }}
                  data-testid="triage-starter-chip"
                  disabled={!connected}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            data={items}
            itemContent={itemContent}
            followOutput={(isAtBottom) => (isAtBottom ? 'smooth' : false)}
            atBottomStateChange={setAtBottom}
            atBottomThreshold={80}
            className={s.messages}
          />
        )}

        {showError && (
          <div className={s.errorRow} role="alert" data-testid="triage-chat-error">
            <span className={s.errorText}>{error}</span>
            {lastUserText && (
              <button
                type="button"
                className={s.errorRetry}
                onClick={() => onSend(lastUserText)}
                data-testid="triage-error-retry"
              >
                Retry
              </button>
            )}
            <button
              type="button"
              className={s.errorDismiss}
              onClick={() => setDismissedError(error)}
              aria-label="Dismiss error"
              data-testid="triage-error-dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {!atBottom && !showEmptyState && (
          <button
            type="button"
            className={s.jumpButton}
            onClick={() =>
              virtuosoRef.current?.scrollToIndex({ index: 'LAST', behavior: 'smooth' })
            }
            aria-label="Jump to latest"
            data-testid="triage-jump-latest"
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </button>
        )}
      </div>

      {followUps.length > 0 && (
        <div className={s.followUpRow}>
          {followUps.map((f) => (
            <button
              key={f}
              type="button"
              className={s.followUpChip}
              onClick={() => onSend(f)}
              data-testid="triage-followup-chip"
            >
              {f}
            </button>
          ))}
        </div>
      )}

      <div className={s.inputArea}>
        <div className={s.mentionChip}>
          <span className={s.mentionDot} />
          <span className={s.mentionLabel}>⚡ @monitoring</span>
          <span className={s.mentionActive}>active</span>
        </div>
        <div className={s.inputRow}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder={composerPlaceholder}
            value={draft}
            disabled={composerDisabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            inputProps={{ 'data-testid': 'triage-chat-input' }}
          />
          {streaming ? (
            <IconButton
              color="primary"
              onClick={() => onStop?.()}
              data-testid="triage-chat-stop"
              aria-label="Stop generating"
              sx={sendButtonSx}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              onClick={submit}
              disabled={composerDisabled || !draft.trim()}
              data-testid="triage-chat-send"
              aria-label="Send message"
              sx={sendButtonSx}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

const sendButtonSx = {
  bgcolor: 'primary.main',
  color: '#fff',
  borderRadius: '50%',
  width: 36,
  height: 36,
  flexShrink: 0,
  '&:hover': { bgcolor: 'primary.dark' },
  '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
} as const;
